import { Shield, Lock, Eye, Database, Users, Mail, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      {/* Header */}
      <section className="hero-gradient py-16 md:py-24" data-testid="privacy-header">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#10B981]/10 rounded-full mb-6">
            <Shield size={32} className="text-[#10B981]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="privacy-heading">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-600" data-testid="privacy-updated">
            Last updated: December 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24" data-testid="privacy-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-slate max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Introduction</h2>
              <p className="text-slate-600 leading-relaxed">
                Snappadeal ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services, including our <strong>TRENDO</strong> brand products sold on Amazon.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Database size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Information We Collect</h2>
              </div>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  <strong>Personal Information:</strong> When you contact us or submit an inquiry, we may collect your name, email address, phone number, and any other information you choose to provide.
                </p>
                <p className="leading-relaxed">
                  <strong>Usage Data:</strong> We automatically collect certain information when you visit our website, including your IP address, browser type, pages visited, and time spent on pages.
                </p>
                <p className="leading-relaxed">
                  <strong>Cookies:</strong> We use cookies and similar tracking technologies to enhance your experience on our website.
                </p>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Eye size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">How We Use Your Information</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>To respond to your inquiries and provide customer support</li>
                <li>To process and fulfill orders for TRENDO products</li>
                <li>To send you updates about our products and services (with your consent)</li>
                <li>To improve our website and services</li>
                <li>To comply with legal obligations</li>
                <li>To provide software development and testing consulting services as requested</li>
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Users size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Information Sharing</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>With service providers who assist us in operating our business</li>
                <li>To comply with legal requirements or respond to lawful requests</li>
                <li>To protect our rights, privacy, safety, or property</li>
                <li>In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Lock size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Data Security</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Your Rights</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>Right to access your personal information</li>
                <li>Right to correct inaccurate information</li>
                <li>Right to request deletion of your information</li>
                <li>Right to opt-out of marketing communications</li>
                <li>Right to data portability</li>
              </ul>
            </div>

            {/* Amazon Purchases */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <ShoppingCart size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Amazon Purchases (TRENDO Brand)</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                When you purchase TRENDO products through Amazon, your transaction is governed by Amazon's privacy policy. We receive limited order information from Amazon to fulfill your order and provide customer support. Please review Amazon's privacy policy for details on how they handle your information.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-slate-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Mail size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Contact Us</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <a 
                href="mailto:contact@snappadeal.com" 
                className="text-[#10B981] hover:text-[#059669] font-medium"
                data-testid="privacy-contact-email"
              >
                contact@snappadeal.com
              </a>
            </div>

            {/* Back Link */}
            <div className="mt-12 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-[#10B981] hover:text-[#059669] font-medium"
                data-testid="privacy-back-link"
              >
                &larr; Back to Home
              </Link>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
